import {defineStore} from 'pinia'
import {useItemIdsStore} from '@/stores/itemIds'
import {useComponentsStore} from '@/stores/components'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'

export const usePreloadViewItemDataStore = defineStore('preloadViewItemData', {
    state: () => ({
        visibleColumns: undefined,
        itemObjs: undefined,
    }),
    
    actions: {
        async get({tableName, visibleColumns, objs}) {
            
            this.visibleColumns = visibleColumns
            this.itemObjs = objs
            
            if (this.itemObjs?.length) {
                // Load the component's structure and determine the file fields.
                const componentsStore = useComponentsStore()
                const componentId = componentsStore.componentIds?.[tableName]
                
                const componentStructureStore = useComponentStructureStore()
                const structureObjs = componentStructureStore.get(componentId) || []
                await this.preloadRelationshipItems(structureObjs)
                await this.preloadFiles(structureObjs)
            }
        },
        
        async preloadRelationshipItems(structureObjs) {
            // Assign all ids stored in single category fields to their componentId.
            // i.e. { categoryComponentId => [itemId, itemId], categoryComponentId => [itemId, itemId] }
            // e.g. { 123 => [1,2,3], 456 => [1,2,3] }
            const singleCategoryObjs = structureObjs.filter((obj) => {
                return obj.type === 'relationshipOneToMany'
            })
            
            const componentIds = {}
            this.itemObjs.forEach((itemObj) => {
                singleCategoryObjs.forEach((categoryObj) => {
                    // Only preload data for fields which are presented in the listing.
                    if (this.visibleColumns.indexOf(categoryObj.id) > -1) {
                        const componentId = categoryObj.categoryComponentId
                        const columnName = categoryObj.columnName
                        
                        const itemId = parseInt(itemObj[columnName])
                        if (itemId) {
                            if (!componentIds[componentId]) {
                                componentIds[componentId] = []
                            }
                            
                            if (componentIds[componentId].indexOf(itemId) === -1) {
                                componentIds[componentId].push(itemId)
                            }
                        }
                    }
                })
            })
            
            // Now preload each category component's items and add them to ItemData.
            const keys = Object.keys(componentIds)
            if (keys.length) {
                const itemIdsStore = useItemIdsStore()
                const componentsStore = useComponentsStore()
                const itemDataStore = useItemDataStore()
                
                for (let componentId of keys) {
                    componentId = parseInt(componentId)
                    const tableName = componentsStore.items?.find((o) => o.id === componentId)?.tableName
                    let ids = componentIds[componentId]
                    
                    if (['odp_courses', 'odp_event', 'odp_schools'].includes(tableName)) {
                        ids = await itemIdsStore.get({tableName})
                    }
                    
                    await itemDataStore.preload({tableName, ids})
                }
            }
        },
        
        async preloadFiles(structureObjs) {
            // Get the column names of all file and image columns in state.visibleColumns
            const columnNames = structureObjs
                .filter((obj) => {
                    return (
                        this.visibleColumns.indexOf(obj.id) > -1 &&
                        ['file', 'image'].indexOf(obj.type) > -1
                    )
                })
                .map((o) => o.columnName)
            
            const fileIds = []
            this.itemObjs.forEach((obj) => {
                columnNames.forEach((columnName) => {
                    const fileId = parseInt(obj[columnName])
                    if (fileId) {
                        fileIds.push(fileId)
                    }
                })
            })
            
            if (fileIds.length) {
                const itemDataStore = useItemDataStore()
                await itemDataStore.preload({
                    tableName: 'files',
                    ids: fileIds,
                })
            }
        },
    },
})